import { useState } from 'react';
import { LayoutCanvas, LayoutObject } from '../../../types/types';
import { LayoutContentError } from '../../../utils/layouts/validateLayoutContent';

type UseLayoutSaveErrors = {
  saveErrors: LayoutContentError[];
  // Bumped on every failed save validation — a one-shot signal for the panel
  // to open the offending section and focus the first invalid field (the
  // saveErrors array itself changes on every edit, so it can't serve).
  saveErrorsVersion: number;
  setSaveErrors: (errors: LayoutContentError[]) => void;
  // Failed validation: keeps the errors and bumps the version.
  reportSaveErrors: (errors: LayoutContentError[]) => void;
  clearObjectFieldErrors: (index: number, patch: Partial<LayoutObject>) => void;
  clearCanvasFieldErrors: (patch: Partial<LayoutCanvas>) => void;
};

// Pending save-validation errors of the layout editor. Editing a field discards
// its own error (revalidated on the next save), so the message never outlives
// the value it complained about.
export const useLayoutSaveErrors = (): UseLayoutSaveErrors => {
  const [saveErrors, setSaveErrors] = useState<LayoutContentError[]>([]);
  const [saveErrorsVersion, setSaveErrorsVersion] = useState(0);

  const reportSaveErrors = (errors: LayoutContentError[]): void => {
    setSaveErrors(errors);
    setSaveErrorsVersion((version) => version + 1);
  };

  const clearObjectFieldErrors = (
    index: number,
    patch: Partial<LayoutObject>
  ): void => {
    setSaveErrors((current) =>
      current.filter(
        (saveError) =>
          !(
            saveError.scope === 'object' &&
            saveError.index === index &&
            patch[saveError.field] !== undefined
          )
      )
    );
  };

  const clearCanvasFieldErrors = (patch: Partial<LayoutCanvas>): void => {
    setSaveErrors((current) =>
      current.filter(
        (saveError) =>
          !(
            saveError.scope === 'canvas' && patch[saveError.field] !== undefined
          )
      )
    );
  };

  return {
    saveErrors,
    saveErrorsVersion,
    setSaveErrors,
    reportSaveErrors,
    clearObjectFieldErrors,
    clearCanvasFieldErrors,
  };
};
